import { Injectable, computed, signal } from '@angular/core';
import {
  ALL_SHELL_VIEW_MODES,
  ShellViewMode,
  getBaseShellViewMode,
  hasNoHeaderInfo,
} from '../interfaces/shell-view-mode';
import { getRuntimeConfig, getShowHeaderInfo } from './runtime-config';

const STORAGE_KEY = 'NODELETE-sia-view-mode';

function readViewModeFromStorage(): ShellViewMode {
  // Il runtime-config (eventualmente arricchito dagli override per-app PWA)
  // ha sempre la precedenza: e' la fonte autoritativa lato installazione.
  // localStorage interviene solo come fallback se il runtime-config non ha un valore valido.
  const fromRuntime = getRuntimeConfig().viewMode;
  if (fromRuntime && ALL_SHELL_VIEW_MODES.includes(fromRuntime)) {
    return fromRuntime;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && ALL_SHELL_VIEW_MODES.includes(stored as ShellViewMode)) {
      return stored as ShellViewMode;
    }
  } catch {
    // localStorage non disponibile (es. iframe sandboxed)
  }
  return fromRuntime ?? 'full';
}

@Injectable({
  providedIn: 'root',
})
export class SiaShellViewModeService {
  viewMode = signal<ShellViewMode>(readViewModeFromStorage());

  baseViewMode = computed(() => getBaseShellViewMode(this.viewMode()));

  // Precedenza: il flag runtime-config `ui.showHeaderInfo === false` nasconde
  // sempre il blocco titolo/sottotitolo. Altrimenti si applica la logica legacy
  // basata su `viewMode: '... noheaderinfo'`. Cosi' i file `runtime-config.json`
  // legacy continuano a funzionare e i clienti gia' configurati non regrediscono.
  showHeaderInfo = computed(() => (getShowHeaderInfo() === false ? false : !hasNoHeaderInfo(this.viewMode())));

  showTopbar = computed(() => this.baseViewMode() === 'full');

  showSidebar = computed(() => this.baseViewMode() === 'full' || this.baseViewMode() === 'sidebar');

  showHeaderbar = computed(
    () => this.baseViewMode() === 'full' || this.baseViewMode() === 'clean' || this.baseViewMode() === 'sidebar',
  );

  setViewMode(mode: ShellViewMode): void {
    this.viewMode.set(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage non disponibile
    }
  }

  removeViewMode() {
    localStorage.removeItem(STORAGE_KEY);
  }
}
